import { useState, useEffect } from "react";
import { fetchMenuTags } from "@/services/menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MenuTag } from "@/types/menu";
import { FormSection } from "./FormSection";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { X, Plus } from "lucide-react";
import { getTagNames } from "@/utils/tagUtils";

interface MenuItemTagsProps {
  tags: string[];
  availableTags: MenuTag[];
  onTagsChange: (tags: string[]) => void;
}

export function MenuItemTags({
  tags,
  availableTags,
  onTagsChange,
}: MenuItemTagsProps) {
  const [newTagName, setNewTagName] = useState<string>("");
  const { t } = useTranslation();

  const handleAddNewTag = () => {
    const tagName = newTagName.trim();
    if (!tagName) return;

    // Check if tag already exists
    const existingTag = availableTags.find(
      (tag) => tag.label.toLowerCase() === tagName.toLowerCase()
    );

    if (existingTag) {
      // Tag exists, add it if not already selected
      if (!tags.includes(existingTag.value)) {
        onTagsChange([...tags, existingTag.value]);
      }
    }

    setNewTagName("");
  };

  const handleRemoveTag = (tagId: string) => {
    onTagsChange(tags.filter((id) => id !== tagId));
  };

  // Helper function to get tag name from ID
  const getTagName = (tagId: string): string => {
    const tag = availableTags.find((t) => t.value === tagId);
    return tag ? tag.label : tagId;
  };

  return (
    <FormSection title={t("tags")} htmlFor="tags">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {tags.map((tagId) => (
            <Badge
              key={tagId}
              variant="secondary"
              className="flex items-center gap-1"
            >
              {getTagName(tagId)}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveTag(tagId)}
              />
            </Badge>
          ))}
        </div>

        <div>
          <p className="text-sm mb-1">{t("menuItemTags.common")}</p>
          <div className="flex flex-wrap gap-1">
            {availableTags
              .filter((tag) => !tags.includes(tag.value))
              .map((tag) => (
                <Badge
                  key={tag.value}
                  variant="outline"
                  className="cursor-pointer hover:bg-primary/10"
                  onClick={() => onTagsChange([...tags, tag.value])}
                >
                  {tag.label}
                </Badge>
              ))}
          </div>
        </div>
      </div>
    </FormSection>
  );
}
